import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Clock, Users, Briefcase, PlaneLanding, Wallet, ShieldCheck } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { api, apiError, eur } from "@/lib/api";
import { vehicleImage, FLEET_FALLBACK } from "@/lib/vehicleImages";
import { useI18n } from "@/i18n/LanguageContext";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { BookingSearch } from "@/components/BookingSearch";

const HERO = "/landing/hero.jpg";

const IOS_URL = "https://apps.apple.com/tr/app/vaya-transfer/id6800821195?l=tr";
const ANDROID_URL = "https://play.google.com/store/apps/details?id=com.vayatransfer.app&hl=en";

const AppleGlyph = (
  <svg viewBox="0 0 384 512" className="h-7 w-7 shrink-0 fill-white" aria-hidden="true">
    <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" />
  </svg>
);

const PlayGlyph = (
  <svg viewBox="0 0 512 512" className="h-7 w-7 shrink-0" aria-hidden="true">
    <path fill="#00E3FF" d="M47 24c-6 3-10 9-10 18v428c0 9 4 15 10 18l1 1 240-240v-3L48 23l-1 1z" />
    <path fill="#00C853" d="M368 335 288 256v-3l80-80 2 1 95 54c27 15 27 41 0 56l-95 53-2-2z" />
    <path fill="#FF3D00" d="M370 333 288 253 47 494c9 9 24 10 40 1l283-162" />
    <path fill="#FFC107" d="M370 179 87 17c-16-9-31-8-40 1l241 241 82-80z" />
  </svg>
);

const StoreBadge = ({ href, top, bottom, glyph, testId }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    data-testid={testId}
    className="flex items-center gap-3 rounded-xl border border-white/15 bg-black px-5 py-3 text-white transition-transform duration-200 hover:scale-[1.03]"
  >
    {glyph}
    <span className="text-left leading-tight">
      <span className="block text-[10px] font-medium uppercase tracking-wide text-white/70">{top}</span>
      <span className="-mt-0.5 block text-lg font-semibold">{bottom}</span>
    </span>
  </a>
);

const DESTS = [
  {
    id: "kemer-center",
    name: "Kemer & Beldibi",
    img: "/landing/kemer.jpg",
  },
  {
    id: "belek",
    name: "Belek & Side",
    img: "/landing/belek.jpg",
  },
  {
    id: "alanya-center",
    name: "Alanya & Gazipaşa",
    img: "/landing/alanya.jpg",
  },
];

export default function Landing() {
  const { t } = useI18n();
  const [classes, setClasses] = useState([]);
  const [routes, setRoutes] = useState([]);

  useEffect(() => {
    api
      .get("/vehicle-classes")
      .then(({ data }) => setClasses(data.classes || []))
      .catch((e) => toast.error(apiError(e)));
    const popular = [
      { origin_id: "ayt", destination_id: "kemer-center", label: "AYT → Kemer" },
      { origin_id: "ayt", destination_id: "belek", label: "AYT → Belek" },
      { origin_id: "ayt", destination_id: "side", label: "AYT → Side" },
      { origin_id: "gzp", destination_id: "alanya-center", label: "GZP → Alanya" },
    ];
    Promise.all(
      popular.map((p) =>
        api
          .post("/quote", { origin_id: p.origin_id, destination_id: p.destination_id, pax: 2 })
          .then(({ data }) => ({
            ...p,
            label: `${data.origin.name.split(" (")[0]} → ${data.destination.name}`,
            km: data.distance_km,
            min: data.duration_min,
            price: (data.options || []).filter((o) => !o.per_person)[0]?.price,
          }))
          .catch(() => null)
      )
    ).then((r) => setRoutes(r.filter(Boolean)));
  }, []);

  const steps = [
    { icon: Clock, title: t("how.s1"), desc: t("how.s1d") },
    { icon: PlaneLanding, title: t("how.s2"), desc: t("how.s2d") },
    { icon: ShieldCheck, title: t("how.s3"), desc: t("how.s3d") },
    { icon: Wallet, title: t("how.s4"), desc: t("how.s4d") },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <section className="relative overflow-hidden">
        <img src={HERO} alt="Antalya coastline" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-slate-950/55" />
        <div className="relative mx-auto max-w-7xl px-5 pb-16 pt-20 lg:px-10 lg:pb-24 lg:pt-28">
          <div className="grid gap-12 md:grid-cols-12">
            <div className="md:col-span-6 fade-up">
              <span className="inline-block rounded-full border border-white/30 bg-white/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-white/90">
                {t("hero.badge")}
              </span>
              <h1 className="mt-6 font-display text-4xl font-medium leading-none tracking-tight text-white sm:text-5xl lg:text-6xl">
                {t("hero.title")}
              </h1>
              <p className="mt-6 max-w-xl text-base leading-relaxed text-white/80">{t("hero.sub")}</p>
              <div className="mt-10 grid max-w-lg grid-cols-3 gap-6 border-t border-white/20 pt-6">
                <div>
                  <p className="font-display text-2xl text-white">60 min</p>
                  <p className="mt-1 text-xs text-white/70">{t("hero.stat1")}</p>
                </div>
                <div>
                  <p className="font-display text-2xl text-white">300</p>
                  <p className="mt-1 text-xs text-white/70">{t("hero.stat2")}</p>
                </div>
                <div>
                  <p className="font-display text-2xl text-white">24/7</p>
                  <p className="mt-1 text-xs text-white/70">{t("hero.stat3")}</p>
                </div>
              </div>
            </div>
            <div className="md:col-span-6 fade-up" style={{ animationDelay: "120ms" }}>
              <BookingSearch />
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-20 lg:px-10 lg:py-24">
        <p className="label-eyebrow">02</p>
        <h2 className="mt-3 font-display text-2xl tracking-tight sm:text-3xl lg:text-4xl">{t("fleet.title")}</h2>
        <p className="mt-3 max-w-2xl text-base text-slate-600">{t("fleet.sub")}</p>
        <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3" data-testid="fleet-grid">
          {classes.map((c) => (
            <article
              key={c.class_id}
              data-testid={`fleet-card-${c.class_id}`}
              className="group overflow-hidden rounded-xl border border-slate-200 bg-white transition-transform duration-300 hover:-translate-y-1 ambient"
            >
              <div className="h-44 overflow-hidden bg-slate-100">
                <img
                  src={vehicleImage(c.class_id, c.image)}
                  alt={c.name}
                  onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = FLEET_FALLBACK; }}
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div className="p-6">
                <h3 className="font-display text-xl">{c.name}</h3>
                <p className="mt-1 text-sm text-slate-500">{c.tagline}</p>
                <div className="mt-4 flex gap-5 text-xs text-slate-600">
                  <span className="inline-flex items-center gap-1.5">
                    <Users size={14} /> {c.capacity}
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <Briefcase size={14} /> {c.luggage}
                  </span>
                </div>
                <p className="mt-5 text-xs text-slate-500">
                  {t("results.total")} · {eur(c.min_fare)}+
                </p>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="bg-white py-20 lg:py-24">
        <div className="mx-auto max-w-7xl px-5 lg:px-10">
          <p className="label-eyebrow">03</p>
          <h2 className="mt-3 font-display text-2xl tracking-tight sm:text-3xl lg:text-4xl">
            {t("routesSec.title")}
          </h2>
          <p className="mt-3 max-w-2xl text-base text-slate-600">{t("routesSec.sub")}</p>
          <div className="mt-12 grid gap-4 md:grid-cols-2 lg:grid-cols-4" data-testid="popular-routes">
            {routes.map((r) => (
              <Link
                key={r.label}
                to={`/results?from=${r.origin_id}&to=${r.destination_id}&pax=2&rt=0&date=${new Date()
                  .toISOString()
                  .slice(0, 10)}&time=10:00`}
                data-testid={`route-card-${r.destination_id}`}
                className="rounded-lg border border-slate-200 bg-white p-6 transition-transform duration-300 hover:-translate-y-1 hover:border-sea"
              >
                <p className="text-sm font-semibold text-slate-900">{r.label}</p>
                <p className="mt-2 text-xs text-slate-500">
                  {r.km} {t("results.km")} · {r.min} {t("results.min")}
                </p>
                <p className="mt-4 font-display text-xl text-sea">{r.price ? eur(r.price) : "—"}</p>
              </Link>
            ))}
          </div>

          <div className="mt-16 grid gap-6 md:grid-cols-3">
            {DESTS.map((d) => (
              <Link
                key={d.id}
                to={`/results?from=ayt&to=${d.id}&pax=2&rt=0`}
                className="group relative h-56 overflow-hidden rounded-xl"
                data-testid={`destination-${d.id}`}
              >
                <img
                  src={d.img}
                  alt={d.name}
                  onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = HERO; }}
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-slate-950/40" />
                <p className="absolute bottom-5 left-5 font-display text-xl text-white">{d.name}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-20 lg:px-10 lg:py-24">
        <p className="label-eyebrow">04</p>
        <h2 className="mt-3 font-display text-2xl tracking-tight sm:text-3xl lg:text-4xl">{t("how.title")}</h2>
        <div className="mt-12 grid gap-10 md:grid-cols-4">
          {steps.map((s, i) => (
            <div key={s.title} data-testid={`how-step-${i}`}>
              <span className="grid h-11 w-11 place-items-center rounded-md bg-sea/10 text-sea">
                <s.icon size={20} />
              </span>
              <h3 className="mt-5 text-xl font-medium">{s.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-slate-950" data-testid="get-app-band">
        <div className="mx-auto max-w-7xl px-5 py-16 lg:px-10 lg:py-20">
          <div className="flex flex-col items-start gap-8 md:flex-row md:items-center md:justify-between">
            <div className="max-w-xl">
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-gold">{t("getapp.eyebrow")}</p>
              <h2 className="mt-3 font-display text-2xl tracking-tight text-white sm:text-3xl lg:text-4xl">{t("getapp.title")}</h2>
              <p className="mt-3 text-base leading-relaxed text-white/70">{t("getapp.sub")}</p>
            </div>
            <div className="flex flex-col gap-6 sm:flex-row">
              <div className="flex flex-col items-center gap-3" data-testid="app-card-ios">
                <div className="rounded-2xl bg-white p-3 shadow-lg" data-testid="ios-qr-code">
                  <QRCodeSVG value={IOS_URL} size={132} level="M" aria-label="iOS app QR code" />
                </div>
                <span className="text-xs text-white/60">{t("getapp.scanIos")}</span>
                <StoreBadge href={IOS_URL} testId="app-store-badge" glyph={AppleGlyph} top={t("getapp.iosTop")} bottom={t("getapp.iosBottom")} />
              </div>
              <div className="flex flex-col items-center gap-3" data-testid="app-card-android">
                <div className="rounded-2xl bg-white p-3 shadow-lg" data-testid="android-qr-code">
                  <QRCodeSVG value={ANDROID_URL} size={132} level="M" aria-label="Android app QR code" />
                </div>
                <span className="text-xs text-white/60">{t("getapp.scanAndroid")}</span>
                <StoreBadge href={ANDROID_URL} testId="google-play-badge" glyph={PlayGlyph} top={t("getapp.androidTop")} bottom={t("getapp.androidBottom")} />
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
